import {
  Dropdown,
  Table,
  Form,
  Button,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { CSVLink } from "react-csv";
import {
  ExportIcon,
  FilterTwoIcon,
  FilterCollapseIcon,
  ExcelIcon,
  PDFIcon,
} from "../../Icons";
import "../../../assets/scss/reports/cashbook.scss";
import { useEffect, useRef, useState } from "react";
import CachedIcon from "mdi-react/CachedIcon";
import Select from "react-select";
import useDebounce, {
  useLocalStorage,
  useQueryParams,
  useScrollTop,
} from "../../../utils/hooks";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { format, parse } from "date-fns";
import ReactPaginate from "react-paginate";
import {
  defaultSelectValue,
  paginationOptions,
  reactSelectTheme,
} from "../../../utils/helpers";
import RsDateRangePicker from "../../utils/RsDateRangePicker";
import queryString from "query-string";
import { useAuth } from "../../../hooks/useAuth";
import { isEmpty } from "lodash";
import NoTableItem from "../../utils/NoTableItem";
import { useDownloadExcel } from "../../../hooks/useDownloadExcel";
import ModalLoader from "../../utils/ModalLoader";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { toast } from "react-toastify";
import PromoIncentiveEarnLogLogModal from "./PromoIncentiveEarnLogLogModal";
import SettlePromoIncentiveModal from "./SettlePromoIncentiveModal";
import PromoIncentivePayoutLogModal from "./PromoIncentivePayoutLogModal";
import currency from "currency.js";

const statusOptions = [
  { label: "All", value: "" },
  { label: "Open", value: "open" },
  { label: "Met", value: "met" },
  { label: "Paid", value: "paid" },
  { label: "Reversed", value: "reversed" },
];

const statusVariant = {
  open: "warning",
  met: "info",
  paid: "success",
  reversed: "danger",
  "partially paid": "success",
};

const money = (v) => currency(v || 0, { symbol: "", separator: "," });
export default function PromoIncentivePayouts() {
  useScrollTop();
  const queryClient = useQueryClient();

  const initialFilterParams = {
    incentiveId: "",
    status: "",
    enableDateRange: true,
  };

  const [payout, setPayout] = useState({});
  const [show, setShow] = useState(false);
  const { backendUrl } = useAuth();
  const [showSettlePromoIncentive, setShowPromoIncentive] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [showFilter, setShowFilter] = useLocalStorage("showFilter", true);
  const [excelData, setExcelData] = useState([]);

  const CSVLinkRef = useRef(null);
  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef,
  );

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const fetchPayouts = async (queryParams) => {
    let response = await fetch(
      `${backendUrl}/api/incentive/promo-payouts?${queryString.stringify(
        queryParams,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };

  const {
    data = {
      count: 0,
      payouts: [],
    },
    refetch,
    isSuccess,
    isFetching,
  } = useQuery(
    ["ATC_INCENTIVE_PAYOUTS", queryParams],
    () => fetchPayouts(queryParams),
    {
      keepPreviousData: true,
    },
  );

  const { limit, page, ...rest } = queryParams;
  const onDownloadExcelData = async () => {
    let exData = await fetchExcelData(
      `${backendUrl}/api/incentive/promo-payouts?${queryString.stringify(
        rest,
      )}`,
      "GET",
    );

    const company = exData.data?.company?.CompName;

    exData = (exData.data?.payouts || []).map((d) => [
      d?.promo_incentive?.Name ?? "",
      d?.promo_incentive?.IncentiveType ?? "",
      d?.promo_incentive?.Threshold || "",
      d?.promo_incentive?.Claim || "",
      money(d?.claimAmount ?? 0),
      money(d?.claimedAmount ?? 0),
      money(d?.remainingClaim ?? 0),
      d.status || "",
      d.metAt ? format(new Date(d.metAt), "MMM dd, yyyy") : "",
      d.paidAt ? format(new Date(d.paidAt), "MMM dd, yyyy") : "",
    ]);

    const date =
      rest.startDate && rest.endDate
        ? `Date Prepared: Between ${format(
            new Date(rest.startDate),
            "E MMM d yyyy",
          )} to ${format(new Date(rest.endDate), "E MMM d yyyy")}`
        : "";

    exData = [
      [company],
      ["ATC Incentive Promo Incentive Report"],
      [date],
      [""],
      [
        "Incentive Title",
        "Type",
        "Buy",
        "Get",
        "Claim Amount",
        "Claimed",
        "Claims Remaining",
        "Status",
        "Met At",
        "Last Claimed At",
      ],
      ...exData,
    ];

    setExcelData(exData);
  };
  const handleFilterParamsChange = (e) => {
    setFilterParams({
      ...filterParams,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearchQueryChange = (e) => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const filterByDateRange = (date) => {
    setFilterParams({
      ...filterParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
      enableDateRange: true,
    });
  };

  const clearDateRange = () => {
    setFilterParams({
      ...filterParams,
      startDate: "",
      endDate: "",
    });
  };

  const search = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const reset = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  return (
    <main className="cash-book">
      <div className="content">
        <div className="content-main">
          <div className={`filter-area ${showFilter ? "open" : "close"}`}>
            <div className="filter-header">
              <h2>
                <FilterTwoIcon /> Filters
              </h2>
              <Button
                variant="white"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FilterCollapseIcon />
              </Button>
            </div>
            <div className="filter-body">
              <Form>
                <div className="body">
                  <Form.Group className="mb-2-5">
                    <Form.Label>Incentive ID</Form.Label>
                    <Form.Control
                      name="incentiveId"
                      placeholder="Search Incentive ID..."
                      value={filterParams.incentiveId}
                      onChange={(e) => handleFilterParamsChange(e)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Status</Form.Label>
                    <Select
                      classNamePrefix="form-select"
                      menuPosition="fixed"
                      menuPlacement="auto"
                      placeholder="All"
                      name="status"
                      theme={reactSelectTheme}
                      isSearchable={false}
                      onChange={(selected) => {
                        setFilterParams({
                          ...filterParams,
                          status: selected.value,
                        });
                      }}
                      defaultValue={defaultSelectValue(
                        queryParams?.status,
                        statusOptions,
                        { value: "", label: "All" },
                      )}
                      options={statusOptions}
                    />
                  </Form.Group>

                  <hr className="mt-1 mb-4" />

                  <Form.Group className="">
                    <Form.Label>Met Date Range</Form.Label>
                    <div className="position-relative">
                      <RsDateRangePicker
                        placement="auto"
                        value={
                          filterParams.startDate && filterParams.endDate
                            ? [
                                parse(
                                  filterParams.startDate,
                                  "yyyy-MM-dd",
                                  new Date(),
                                ),
                                parse(
                                  filterParams.endDate,
                                  "yyyy-MM-dd",
                                  new Date(),
                                ),
                              ]
                            : []
                        }
                        onClean={() => clearDateRange()}
                        onOk={(date) => filterByDateRange(date)}
                      />
                    </div>
                  </Form.Group>
                </div>

                <div className="buttons rounded">
                  <Button onClick={() => reset()} variant="white">
                    Reset
                  </Button>
                  <Button onClick={() => search()} variant="primary">
                    Search
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          <div className="content-body">
            <header>
              <h1>
                {!showFilter && (
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    className="btn filter"
                  >
                    <FilterTwoIcon />
                  </button>
                )}
                Promo Incentive Claims
                <button onClick={() => refetch()} className="btn text-primary">
                  <CachedIcon />
                </button>
              </h1>
              <div className="actions mr-5">
                <CSVLink
                  className="btn print d-none"
                  filename={`Promo Incentive Claims (${format(
                    new Date(),
                    "dd-MMM-yyyy hh:mm:ss a",
                  )}).csv`}
                  data={excelData}
                  ref={CSVLinkRef}
                />

                <Dropdown>
                  <Dropdown.Toggle
                    variant=""
                    className="btn print"
                    disabled={isfetchingExcel}
                    bsPrefix=""
                  >
                    <ExportIcon color="#008000" />
                    Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{ strategy: "fixed" }}
                    renderOnMount
                    className="text-center"
                  >
                    <Dropdown.Item
                      as="div"
                      onClick={onDownloadExcelData}
                      className="p-cursor"
                    >
                      Excel <ExcelIcon color="#008000" />
                    </Dropdown.Item>
                    {/* <Dropdown.Item as="div">
                      <a
                        href={`${backendUrl}/api/incentive/promo-payouts/pdf?${queryString.stringify(
                          rest,
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PDF <PDFIcon color="#ff0000" />
                      </a>
                    </Dropdown.Item> */}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </header>

            <div className="px-md-4">
              <Table responsive borderless striped className="product-table">
                <thead>
                  <tr>
                    <th>{"  "}</th>
                    <th>Incentive Title</th>
                    <th>Type</th>
                    <th>Buy</th>
                    <th>Get</th>
                    <th>Claim Amount</th>
                    <th>Claimed </th>
                    <th>Claims Remaining</th>
                    <th>Status</th>
                    <th>Met At</th>
                    <th>Last Claimed At</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payouts.map((el, index) => (
                    <tr key={el.id || index}>
                      <td>
                        {"  "}
                        <Dropdown drop="end">
                          <Dropdown.Toggle
                            variant=""
                            className="bg-white border-0"
                            bsPrefix="print more"
                          >
                            <DotsVerticalIcon />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            popperConfig={{ strategy: "fixed" }}
                            renderOnMount
                            className="dropdown-with-icons"
                          >
                            {["met"].includes(el.status) ? (
                              <Dropdown.Item
                                as="button"
                                disabled={
                                  !["met", "partially paid"].includes(el.status)
                                }
                                onClick={() => {
                                  setPayout(el);
                                  setShowPromoIncentive(true);
                                }}
                              >
                                Settle Claim
                              </Dropdown.Item>
                            ) : null}
                            <Dropdown.Item
                              as="button"
                              onClick={() => {
                                setPayout(el);
                                setShow(true);
                              }}
                            >
                              Claim Log
                            </Dropdown.Item>
                            <Dropdown.Item
                              as="button"
                              onClick={() => {
                                setPayout(el);
                                setShowClaim(true);
                              }}
                            >
                              Claimed Payout Log
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>{el?.promo_incentive?.Name ?? ""}</td>
                      <td>{el?.promo_incentive?.IncentiveType ?? ""}</td>
                      <td className="text-capitalize">
                        {el?.promo_incentive?.Threshold || "_"}
                      </td>
                      <td className="text-capitalize">
                        {el?.promo_incentive?.Claim || "_"}
                      </td>
                      <td className="fw-bold">{el?.claimAmount ?? ""}</td>
                      <td className="fw-bold">{el?.claimedAmount ?? ""}</td>
                      <td className="fw-bold">{el?.remainingClaim ?? ""}</td>

                      <td>
                        <Badge bg={statusVariant[el.status] || "secondary"}>
                          {el.status || "_"}
                        </Badge>
                      </td>

                      <td className="text-nowrap">
                        {el.metAt
                          ? format(new Date(el.metAt), "MMM dd, yyyy")
                          : "_"}
                      </td>

                      <td className="text-nowrap">
                        {el.paidAt
                          ? format(new Date(el.paidAt), "MMM dd, yyyy")
                          : "_"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {!isFetching && isSuccess && isEmpty(data?.payouts) ? (
                <NoTableItem queryParams={queryParams} />
              ) : null}
            </div>

            <div className="d-flex justify-content-between px-3 align-items-center pagination">
              <div className="pagination_left">
                <p className="m-0 p-0">Show</p>
                <select
                  value={queryParams.limit}
                  name="limit"
                  className="form-select"
                  onChange={(e) => handleSearchQueryChange(e)}
                >
                  <option value="10">10 rows</option>
                  <option value="20">20 rows</option>
                  <option value="30">30 rows</option>
                  <option value="40">40 rows</option>
                  <option value="50">50 rows</option>
                  <option value="100">100 rows</option>
                </select>
              </div>

              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(data.count / queryParams.limit)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={0}
                onPageChange={({ selected }) => {
                  document.body.scrollTop = document.documentElement.scrollTop = 0;
                  setQueryParams({
                    ...queryParams,
                    page: selected + 1,
                  });
                }}
                forcePage={queryParams.page - 1}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalLoader show={isfetchingExcel} />
      {show && (
        <PromoIncentiveEarnLogLogModal
          show={show}
          onHide={setShow}
          payout={payout}
        />
      )}

      {showSettlePromoIncentive && (
        <SettlePromoIncentiveModal
          onClose={setShowPromoIncentive}
          payout={payout}
          show={showSettlePromoIncentive}
          refetch={refetch}
        />
      )}

      {showClaim && (
        <PromoIncentivePayoutLogModal
          show={showClaim}
          onHide={setShowClaim}
          payout={payout}
        />
      )}
    </main>
  );
}
